import React, { FC, useState } from "react";
import { LuCheckCircle2 } from "react-icons/lu";

import {
  AiBugReportResponse,
  useSendBugReportMutation,
} from "../../../slices/AiSlice";
import { aiErrorMessage } from "../ai.utils";
import { Spinner } from "../WriteWithAi";
import {
  appVersion,
  browserInfo,
  charactersLeftText,
  redactPageUrl,
  REPORT_EXPECTED_MAX,
  REPORT_WHAT_MAX,
} from "./helpChat.utils";

type Props = {
  orgId: string;
  /** "You: … / Assistant: …" when the report starts from the chat. */
  chatExcerpt?: string;
  onCancel: () => void;
  onDone: () => void;
};

const label = "mb-1.5 block text-[12.5px] font-bold text-[#1F2433]";
const field =
  "w-full rounded-[10px] border-[1.5px] border-[#DCE2EA] bg-white px-3 py-2 text-[13.5px] leading-[1.45] text-[#1F2433] outline-none placeholder:text-[#A3A9B8] focus:border-[#6B4EFF]";
export const primaryButton =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-[#6B4EFF] px-4 py-2 text-[13px] font-bold text-white shadow-[0_3px_10px_rgba(107,78,255,0.25)] hover:bg-[#5A3FE6] disabled:cursor-not-allowed disabled:opacity-50";
export const secondaryButton =
  "inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-[#DCE2EA] bg-white px-4 py-2 text-[13px] font-bold text-[#1F2433] hover:bg-[#F6F7FA]";

/** "Report a problem" inside the help panel (works even when AI is off). */
/** "N characters left" under a box near its limit. */
const CharsLeft: FC<{ value: string; max: number }> = ({ value, max }) => {
  const text = charactersLeftText(value, max);
  return text ? (
    <div
      className={"mt-1 text-right text-[11.5px] text-[#8A90A2]"}
      aria-live={"polite"}>
      {text}
    </div>
  ) : null;
};

export const HelpReportForm: FC<Props> = ({
  orgId,
  chatExcerpt,
  onCancel,
  onDone,
}) => {
  const [send, { isLoading }] = useSendBugReportMutation();
  const [whatHappened, setWhatHappened] = useState("");
  const [expected, setExpected] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AiBugReportResponse | null>(null);
  const page = redactPageUrl(window.location.href);

  const submit = async () => {
    if (!whatHappened.trim()) {
      setError("Please tell us what happened.");
      return;
    }
    setError(null);
    try {
      const response = await send({
        orgId,
        whatHappened: whatHappened.trim(),
        expected: expected.trim() || undefined,
        page,
        chatExcerpt: chatExcerpt || undefined,
        browser: browserInfo(),
        appVersion: appVersion(),
      }).unwrap();
      setResult(response);
    } catch (caught) {
      setError(
        aiErrorMessage(caught).replace(
          "AI couldn't answer right now.",
          "Your report couldn't be sent.",
        ),
      );
    }
  };

  if (result) {
    const delivered = result.emailed || result.savedToSheet;
    return (
      <div
        className={
          "flex flex-1 flex-col items-center justify-center px-8 text-center"
        }>
        <div
          className={
            "flex h-14 w-14 items-center justify-center rounded-full bg-[#E7F6EE] text-[#22A06B]"
          }>
          <LuCheckCircle2 size={30} strokeWidth={2} />
        </div>
        <div className={"mt-4 text-[16px] font-extrabold text-[#1F2433]"}>
          Thanks!
        </div>
        <div className={"mt-1.5 text-[13.5px] leading-[1.5] text-[#3A4256]"}>
          {delivered
            ? "Your report was sent to the ProCharted team."
            : "Your report was saved for the ProCharted team."}
        </div>
        {(result.reportCode || result.reportId) && (
          <div className={"mt-2 text-[12px] text-[#8A90A2]"}>
            Report number: {result.reportCode || result.reportId}
          </div>
        )}
        <button
          type={"button"}
          className={`${secondaryButton} mt-6`}
          onClick={onDone}>
          Back to help
        </button>
      </div>
    );
  }

  return (
    <form
      className={"flex min-h-0 flex-1 flex-col"}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}>
      <div className={"min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4"}>
        <div>
          <div className={"text-[15px] font-extrabold text-[#1F2433]"}>
            Report a problem
          </div>
          <div className={"mt-1 text-[12.5px] leading-[1.45] text-[#6B7185]"}>
            Tell us what went wrong and we&apos;ll look into it.
          </div>
        </div>
        <div>
          <label className={label} htmlFor={"help-report-what"}>
            What happened? <span className={"text-[#E5484D]"}>*</span>
          </label>
          <textarea
            id={"help-report-what"}
            rows={4}
            autoFocus
            className={`${field} resize-none`}
            placeholder={
              "For example: I clicked Save on a new client and nothing happened."
            }
            value={whatHappened}
            maxLength={REPORT_WHAT_MAX}
            onChange={(event) => {
              setWhatHappened(event.target.value.slice(0, REPORT_WHAT_MAX));
              if (error) setError(null);
            }}
          />
          <CharsLeft value={whatHappened} max={REPORT_WHAT_MAX} />
        </div>
        <div>
          <label className={label} htmlFor={"help-report-expected"}>
            What did you expect?{" "}
            <span className={"font-semibold text-[#8A90A2]"}>(optional)</span>
          </label>
          <textarea
            id={"help-report-expected"}
            rows={2}
            className={`${field} resize-none`}
            placeholder={"For example: the client to be saved."}
            value={expected}
            maxLength={REPORT_EXPECTED_MAX}
            onChange={(event) =>
              setExpected(event.target.value.slice(0, REPORT_EXPECTED_MAX))
            }
          />
          <CharsLeft value={expected} max={REPORT_EXPECTED_MAX} />
        </div>
        <div>
          <label className={label} htmlFor={"help-report-page"}>
            Page
          </label>
          <input
            id={"help-report-page"}
            readOnly
            value={page}
            className={`${field} truncate bg-[#F6F7FA] text-[#6B7185]`}
          />
        </div>
        <div className={"text-[11.5px] leading-[1.45] text-[#8A90A2]"}>
          Your browser and app version
          {chatExcerpt ? " and the last messages of this chat" : ""} are
          attached to help us find the problem.
        </div>
        {error && (
          <div
            role={"alert"}
            className={
              "rounded-[10px] border border-[#F6C9CB] bg-[#FDF0F0] px-3 py-2 text-[12.5px] font-semibold text-[#C8333A]"
            }>
            {error}
          </div>
        )}
      </div>
      <div
        className={
          "flex justify-end gap-2 border-t border-[#EDEFF3] px-5 py-3"
        }>
        <button type={"button"} className={secondaryButton} onClick={onCancel}>
          Cancel
        </button>
        <button type={"submit"} className={primaryButton} disabled={isLoading}>
          {isLoading && (
            <Spinner className={"!border-white/40 !border-t-white"} />
          )}
          {isLoading ? "Sending…" : "Send"}
        </button>
      </div>
    </form>
  );
};
